// @ts-nocheck
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CheckCircle2, AlertCircle, Loader2, UserPlus, LogIn, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";

interface RosterData {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  team_id: string;
  profile_id: string | null;
}

interface TeamData {
  id: string;
  name: string;
}

const JoinTeam: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, profile, isLoading: authLoading } = useAuth();

  const [loading, setLoading] = useState(true);
  const [rosterData, setRosterData] = useState<RosterData | null>(null);
  const [teamData, setTeamData] = useState<TeamData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [sendingMagicLink, setSendingMagicLink] = useState(false);

  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setError('Invalid invitation link. No token provided.');
      setLoading(false);
      return;
    }

    fetchInvitationData();
  }, [token]);

  useEffect(() => {
    // Once auth is loaded and we have roster data, check if user is logged in
    if (!authLoading && rosterData && user && profile) {
      handleLoggedInUser();
    }
  }, [authLoading, rosterData, user, profile]);

  const fetchInvitationData = async () => {
    try {
      setLoading(true);
      console.log('🔍 [JOIN-TEAM] Fetching invitation data for token:', token);

      // Fetch roster record with team data via join (avoids separate RLS check on coach_teams)
      const { data: roster, error: rosterError } = await supabase
        .from('coach_team_rosters')
        .select(`
          id, first_name, last_name, email, team_id, profile_id,
          coach_teams:team_id (id, name)
        `)
        .eq('id', token)
        .single();

      if (rosterError || !roster) {
        console.error('❌ [JOIN-TEAM] Roster fetch error:', rosterError);
        setError('Invalid invitation link. This roster invitation does not exist.');
        setLoading(false);
        return;
      }

      console.log('✅ [JOIN-TEAM] Roster found:', roster);
      
      // Extract team data from join
      const teamFromRoster = roster.coach_teams as unknown as TeamData | null;
      
      if (!teamFromRoster) {
        console.error('❌ [JOIN-TEAM] Team not found in roster join');
        setError('Unable to load team information.');
        setLoading(false);
        return;
      }

      setRosterData({
        id: roster.id,
        first_name: roster.first_name,
        last_name: roster.last_name,
        email: roster.email,
        team_id: roster.team_id,
        profile_id: roster.profile_id,
      });
      setTeamData(teamFromRoster);
      console.log('✅ [JOIN-TEAM] Team found:', teamFromRoster);
      setLoading(false);
    } catch (err) {
      console.error('❌ [JOIN-TEAM] Exception:', err);
      setError('An unexpected error occurred while loading the invitation.');
      setLoading(false);
    }
  };

  const handleLoggedInUser = async () => {
    if (!rosterData || !profile || processing) return;

    // Check if already linked
    if (rosterData.profile_id === profile.id) {
      console.log('✅ [JOIN-TEAM] Already linked to profile');
      setSuccess(true);
      return;
    }

    // Check email match
    const rosterEmail = rosterData.email?.toLowerCase().trim();
    const profileEmail = profile.email?.toLowerCase().trim();

    console.log('📧 [JOIN-TEAM] Email check:', { rosterEmail, profileEmail });

    if (rosterEmail !== profileEmail) {
      setError(
        `This invitation was sent to ${rosterData.email}. You are logged in as ${profile.email}. Please log out and sign in with the correct account, or contact your coach.`
      );
      return;
    }

    // Email matches - link the profile
    await linkProfileToRoster();
  };

  const linkProfileToRoster = async () => {
    if (!rosterData || !profile) return;

    try {
      setProcessing(true);
      console.log('🔗 [JOIN-TEAM] Linking profile to roster:', {
        rosterId: rosterData.id,
        profileId: profile.id,
      });

      const { error: updateError } = await supabase
        .from('coach_team_rosters')
        .update({
          profile_id: profile.id,
        })
        .eq('id', rosterData.id);

      if (updateError) {
        console.error('❌ [JOIN-TEAM] Profile link error:', updateError);
        toast.error('Failed to link your profile to the team.');
        setError('Failed to add you to the team. Please try again or contact your coach.');
        return;
      }

      console.log('✅ [JOIN-TEAM] Profile linked successfully');
      setSuccess(true);
      toast.success(`You've been added to ${teamData?.name}!`);
    } catch (err) {
      console.error('❌ [JOIN-TEAM] Exception linking profile:', err);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const handleMagicLink = async () => {
    if (!rosterData?.email || !teamData?.name) return;

    try {
      setSendingMagicLink(true);
      console.log('🔗 [JOIN-TEAM] Sending branded magic link to:', rosterData.email);

      // Call custom edge function instead of Supabase OTP
      const { data, error: invokeError } = await invokeEdgeFunction('send-team-invite-magic-link', {
        body: {
          roster_id: rosterData.id,
          email: rosterData.email,
          first_name: rosterData.first_name,
          last_name: rosterData.last_name,
          team_name: teamData.name,
        },
      });

      if (invokeError) {
        console.error('❌ [JOIN-TEAM] Magic link error:', invokeError);
        toast.error('Failed to send magic link. Please try again.');
        return;
      }

      console.log('✅ [JOIN-TEAM] Branded magic link sent successfully:', data);
      setMagicLinkSent(true);
      toast.success('Magic link sent! Check your email.');
    } catch (err) {
      console.error('❌ [JOIN-TEAM] Exception sending magic link:', err);
      toast.error('An unexpected error occurred. Please try again.');
    } finally {
      setSendingMagicLink(false);
    }
  };

  const handleLogin = () => {
    // Redirect to login with return URL
    const returnUrl = `/join-team?token=${token}`;
    navigate(`/auth/login?redirect=${encodeURIComponent(returnUrl)}`);
  };

  const handleSignup = () => {
    // Redirect to athlete signup with roster_id and email as query params
    navigate(`/register?roster_id=${token}&email=${encodeURIComponent(rosterData?.email || '')}&tab=athlete`);
  };

  if (loading || authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="w-full max-w-md">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" />
            <p className="text-muted-foreground">Loading invitation...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <div className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-6 w-6" />
              <CardTitle>Invitation Error</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
            <Button onClick={() => navigate('/')} className="w-full mt-6">
              Go to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <div className="flex items-center gap-2 text-success">
              <CheckCircle2 className="h-6 w-6" />
              <CardTitle>Success!</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert className="border-success text-success">
              <CheckCircle2 className="h-4 w-4" />
              <AlertDescription>
                You've been successfully added to <strong>{teamData?.name}</strong>!
              </AlertDescription>
            </Alert>
            <p className="text-muted-foreground text-sm">
              Your coach can now manage your roster information. You can access your team dashboard to view schedules and updates.
            </p>
            <div className="flex flex-col gap-2">
              <Button onClick={() => navigate('/athlete-dashboard')} className="w-full">
                Go to Dashboard
              </Button>
              <Button onClick={() => navigate('/')} variant="outline" className="w-full">
                Go to Home
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Magic link sent - show confirmation
  if (magicLinkSent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <div className="flex items-center gap-2 text-primary">
              <Mail className="h-6 w-6" />
              <CardTitle>Check Your Email</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert>
              <Mail className="h-4 w-4" />
              <AlertDescription>
                We've sent a magic link to <strong>{rosterData?.email}</strong>
              </AlertDescription>
            </Alert>
            <p className="text-muted-foreground text-sm">
              Click the link in your email to sign in and join <strong>{teamData?.name}</strong>. 
              The link will expire in 1 hour.
            </p>
            <div className="flex flex-col gap-2">
              <Button onClick={handleMagicLink} variant="outline" className="w-full" disabled={sendingMagicLink}>
                {sendingMagicLink ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  'Resend Magic Link'
                )}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground text-center">
              Didn't receive the email? Check your spam folder or click resend.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // User is not logged in - show options
  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Team Invitation</CardTitle>
            <CardDescription>
              You've been invited to join <strong>{teamData?.name}</strong>
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="bg-muted p-4 rounded-lg">
              <p className="text-sm text-muted-foreground mb-2">Invited as:</p>
              <p className="font-medium">
                {rosterData?.first_name} {rosterData?.last_name}
              </p>
              <p className="text-sm text-muted-foreground">{rosterData?.email}</p>
            </div>

            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">To accept this invitation:</p>

              <Button onClick={handleMagicLink} className="w-full" size="lg" disabled={sendingMagicLink}>
                {sendingMagicLink ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Mail className="mr-2 h-4 w-4" />
                    Send me a magic link
                  </>
                )}
              </Button>
              
              <p className="text-xs text-muted-foreground text-center">
                We'll email you a secure link to instantly create your account
              </p>

              <div className="relative my-4">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-background px-2 text-muted-foreground">
                    Or
                  </span>
                </div>
              </div>

              <Button onClick={handleLogin} variant="outline" className="w-full" size="lg">
                <LogIn className="mr-2 h-4 w-4" />
                I already have an account
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // User is logged in but email doesn't match (error state already shown above)
  // This should not be reached due to early return in error state
  return null;
};

export default JoinTeam;